import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea,
  Button, Stack, Chip, Divider, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Alert, CircularProgress, LinearProgress,
  Table, TableHead, TableRow, TableCell, Switch,
  FormControlLabel,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SaveIcon from '@mui/icons-material/Save';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  CEREMONY_TEMPLATES_QUERY,
  UPDATE_CEREMONY_TEMPLATE_MUTATION,
  CREATE_CEREMONY_TEMPLATE_MUTATION,
} from '../graphql/queries';
import ContractedVendorsSection from '../components/ContractedVendorsSection';

const CATEGORIES = ['material', 'decoration', 'ingredient', 'equipment'];
const CAT_COLOR  = { material: 'warning', decoration: 'secondary', ingredient: 'success', equipment: 'info' };
const EMPTY_ITEM = { item_name: '', category: 'material', quantity: '' };
const EMPTY_TMPL = { ceremonyType: '', displayName: '', description: '', sortOrder: 0 };

export default function AdminRitualItemsPage() {
  const [selectedId, setSelectedId]   = useState(null);
  const [items, setItems]             = useState([]);      // working copy of defaultMaterials
  const [dirty, setDirty]             = useState(false);
  const [itemDialog, setItemDialog]   = useState(false);
  const [editIdx, setEditIdx]         = useState(null);    // null = add, number = edit
  const [itemForm, setItemForm]       = useState(EMPTY_ITEM);
  const [tmplDialog, setTmplDialog]   = useState(false);
  const [tmplForm, setTmplForm]       = useState(EMPTY_TMPL);
  const [saveError, setSaveError]     = useState('');

  // ── Data ─────────────────────────────────────────────────────
  const { data, loading, refetch } = useQuery(CEREMONY_TEMPLATES_QUERY, {
    fetchPolicy: 'cache-and-network',
    // also fetch inactive templates for admin
    variables: {},
  });

  const templates = data?.ceremonyTemplates || [];
  const selected  = templates.find(t => t.id === selectedId) || null;

  const selectTemplate = (tmpl) => {
    setSelectedId(tmpl.id);
    setItems(JSON.parse(JSON.stringify(tmpl.defaultMaterials || [])));
    setDirty(false);
    setSaveError('');
  };

  // ── Mutations ────────────────────────────────────────────────
  const [updateTemplate, { loading: saving }] = useMutation(UPDATE_CEREMONY_TEMPLATE_MUTATION, {
    onCompleted: () => { setDirty(false); refetch(); },
    onError: (e) => setSaveError(e.message),
  });

  const [createTemplate, { loading: creating }] = useMutation(CREATE_CEREMONY_TEMPLATE_MUTATION, {
    onCompleted: (d) => {
      setTmplDialog(false);
      setTmplForm(EMPTY_TMPL);
      refetch().then(() => selectTemplate(d.createCeremonyTemplate));
    },
    onError: (e) => setSaveError(e.message),
  });

  // ── Item list handlers ────────────────────────────────────────
  const openAdd  = () => { setItemForm(EMPTY_ITEM); setEditIdx(null); setItemDialog(true); };
  const openEdit = (idx) => { setItemForm({ ...items[idx] }); setEditIdx(idx); setItemDialog(true); };

  const commitItem = () => {
    if (!itemForm.item_name.trim()) return;
    const next = [...items];
    if (editIdx === null) {
      next.push({ item_name: itemForm.item_name.trim(), category: itemForm.category, quantity: itemForm.quantity || '' });
    } else {
      next[editIdx] = { item_name: itemForm.item_name.trim(), category: itemForm.category, quantity: itemForm.quantity || '' };
    }
    setItems(next);
    setDirty(true);
    setItemDialog(false);
  };

  const removeItem = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    setItems(next);
    setDirty(true);
  };

  const moveItem = (idx, dir) => {
    const next = [...items];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    setDirty(true);
  };

  const handleSave = () => {
    setSaveError('');
    updateTemplate({ variables: { id: selectedId, defaultMaterials: items } });
  };

  // ── Template create ───────────────────────────────────────────
  const handleCreateTemplate = () => {
    if (!tmplForm.ceremonyType.trim() || !tmplForm.displayName.trim()) return;
    createTemplate({
      variables: {
        ceremonyType:  tmplForm.ceremonyType.trim(),
        displayName:   tmplForm.displayName.trim(),
        description:   tmplForm.description || null,
        sortOrder:     Number(tmplForm.sortOrder) || 0,
        defaultMaterials: [],
      },
    });
  };

  // ── Render ───────────────────────────────────────────────────
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Standard Ceremonies</Typography>
          <Typography variant="body2" color="text.secondary">
            The standard ceremonies and their usual items. Rituals vendors start their ceremonies from these in
            My Services and price each item; families then plan a ceremony from their contracted vendor's list.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setTmplDialog(true)}>
          New Template
        </Button>
      </Stack>

      <ContractedVendorsSection category="rituals" />

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {/* ── Left: template list ─────────────────────────── */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ pb: '8px !important' }}>
              <Typography variant="caption" color="text.secondary">CEREMONY TEMPLATES</Typography>
            </CardContent>
            <Divider />
            {templates.length === 0 && !loading && (
              <Box p={2}><Typography variant="body2" color="text.secondary">No templates found.</Typography></Box>
            )}
            {templates.map(tmpl => (
              <CardActionArea
                key={tmpl.id}
                onClick={() => selectTemplate(tmpl)}
                sx={{
                  px: 2, py: 1.5,
                  bgcolor: tmpl.id === selectedId ? 'primary.50' : 'transparent',
                  borderLeft: tmpl.id === selectedId ? '3px solid' : '3px solid transparent',
                  borderColor: tmpl.id === selectedId ? 'primary.main' : 'transparent',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" fontWeight={tmpl.id === selectedId ? 700 : 400}>
                      {tmpl.displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {tmpl.ceremonyType?.replace(/_/g, ' ')}
                    </Typography>
                  </Box>
                  <Chip
                    label={(tmpl.defaultMaterials || []).length}
                    size="small"
                    color={tmpl.id === selectedId ? 'primary' : 'default'}
                    icon={<AutoAwesomeIcon sx={{ fontSize: '12px !important' }} />}
                  />
                </Stack>
              </CardActionArea>
            ))}
          </Card>
        </Grid>

        {/* ── Right: items editor ─────────────────────────── */}
        <Grid item xs={12} md={9}>
          {!selected && (
            <Alert severity="info">Select a ceremony template on the left to manage its default items.</Alert>
          )}

          {selected && (
            <Card>
              <CardContent>
                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>{selected.displayName}</Typography>
                    <Typography variant="body2" color="text.secondary">{selected.description || 'No description'}</Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      size="small"
                      onClick={openAdd}
                    >
                      Add Item
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      size="small"
                      onClick={handleSave}
                      disabled={!dirty || saving}
                      color={dirty ? 'success' : 'inherit'}
                    >
                      {saving ? 'Saving…' : dirty ? 'Save Changes' : 'Saved'}
                    </Button>
                  </Stack>
                </Stack>

                {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

                {dirty && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    You have unsaved changes. Click <strong>Save Changes</strong> to persist.
                  </Alert>
                )}

                {items.length === 0 && (
                  <Alert severity="info">
                    No items defined yet. Click <strong>Add Item</strong> to start building the checklist.
                  </Alert>
                )}

                {items.length > 0 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Item Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Quantity</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 130 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>
                            <Stack spacing={0}>
                              <IconButton size="small" onClick={() => moveItem(idx, -1)} disabled={idx === 0} sx={{ p: 0.25 }}>
                                <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                              <IconButton size="small" onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} sx={{ p: 0.25 }}>
                                <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{item.item_name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.category}
                              size="small"
                              color={CAT_COLOR[item.category] || 'default'}
                              variant="outlined"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{item.quantity || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => openEdit(idx)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => removeItem(idx)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* ── Add / Edit Item Dialog ──────────────────────────── */}
      <Dialog open={itemDialog} onClose={() => setItemDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editIdx === null ? 'Add Item' : 'Edit Item'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Item Name"
              value={itemForm.item_name}
              onChange={e => setItemForm(f => ({ ...f, item_name: e.target.value }))}
              fullWidth
              required
              autoFocus
            />
            <TextField
              select
              label="Category"
              value={itemForm.category}
              onChange={e => setItemForm(f => ({ ...f, category: e.target.value }))}
              fullWidth
            >
              {CATEGORIES.map(c => (
                <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Default Quantity (optional)"
              value={itemForm.quantity}
              onChange={e => setItemForm(f => ({ ...f, quantity: e.target.value }))}
              fullWidth
              placeholder="e.g. 50 pieces, 1 kg, 500 ml"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={commitItem} disabled={!itemForm.item_name.trim()}>
            {editIdx === null ? 'Add' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── New Template Dialog ─────────────────────────────── */}
      <Dialog open={tmplDialog} onClose={() => setTmplDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Ceremony Template</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Ceremony Type (key)"
              value={tmplForm.ceremonyType}
              onChange={e => setTmplForm(f => ({ ...f, ceremonyType: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
              fullWidth
              required
              placeholder="e.g. muhurtham"
              helperText="Lowercase, underscores only"
            />
            <TextField
              label="Display Name"
              value={tmplForm.displayName}
              onChange={e => setTmplForm(f => ({ ...f, displayName: e.target.value }))}
              fullWidth
              required
              placeholder="e.g. Muhurtham (Main Wedding)"
            />
            <TextField
              label="Description"
              value={tmplForm.description}
              onChange={e => setTmplForm(f => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Sort Order"
              type="number"
              value={tmplForm.sortOrder}
              onChange={e => setTmplForm(f => ({ ...f, sortOrder: e.target.value }))}
              fullWidth
            />
          </Stack>
          {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTmplDialog(false); setSaveError(''); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateTemplate}
            disabled={creating || !tmplForm.ceremonyType.trim() || !tmplForm.displayName.trim()}
          >
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
